import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "./DashboardLayout";
import { deleteDonation, DonationRow, fetchDonations, updateDonation } from "./api";
import ActionToast, { ToastAction } from "./ActionToast";
import HeaderBanner from "../images/header1.jpg";
import SignatureImage from "../images/Signature-Of-srisailam.png";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const COUNTRY_OPTIONS = ["India", "Other"];
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const DonationList: React.FC = () => {
  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";
  const [rows, setRows] = useState<DonationRow[]>([]);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [viewRow, setViewRow] = useState<DonationRow | null>(null);
  const [editRow, setEditRow] = useState<DonationRow | null>(null);
  const [showStateOptions, setShowStateOptions] = useState(false);
  const [stateSearchValue, setStateSearchValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [toastAction, setToastAction] = useState<ToastAction | null>(null);

  useEffect(() => {
    fetchDonations()
      .then(setRows)
      .catch((err: Error) => setError(err.message || "Failed to fetch donations"));
  }, []);

  useEffect(() => {
    if (!toastAction) return;
    const timer = setTimeout(() => setToastAction(null), 6500);
    return () => clearTimeout(timer);
  }, [toastAction]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.fullName?.toLowerCase().includes(q) ||
        String(r.receiptNo || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const total = useMemo(() => filtered.reduce((acc, row) => acc + Number(row.amount || 0), 0), [filtered]);

  const onDelete = async (id: number) => {
    if (!window.confirm("Delete this donation?")) return;
    try {
      await deleteDonation(id);
      setRows((prev) => prev.filter((row) => row.id !== id));
      setToastAction("deleted");
    } catch (err: any) {
      alert(err.message || "Delete failed");
    }
  };

  const onSave = async () => {
    if (!editRow) return;
    const matched = INDIAN_STATES.find((option) => option.toLowerCase() === stateSearchValue.trim().toLowerCase());
    const selectedState = (matched || editRow.state || "").trim();
    const hasValidState = INDIAN_STATES.some((option) => option.toLowerCase() === selectedState.toLowerCase());
    if (!hasValidState) {
      alert("Please select a valid state from the dropdown.");
      return;
    }
    const rowToSave = { ...editRow, state: selectedState };
    setSaving(true);
    try {
      const result = await updateDonation(rowToSave);
      const updated = result.donation || rowToSave;
      setRows((prev) => prev.map((item) => (item.id === rowToSave.id ? { ...item, ...updated } : item)));
      setEditRow(null);
      setShowStateOptions(false);
      setStateSearchValue("");
      setToastAction("saved");
    } catch (err: any) {
      alert(err.message || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const toMediaUrl = (value?: string) => {
    if (!value) return "";
    if (value.startsWith("http://") || value.startsWith("https://")) return value;
    return `${API_URL}${value}`;
  };

  const toInputDate = (value?: string) => {
    if (!value) return "";
    const raw = String(value).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
      const [day, month, year] = raw.split("-");
      return `${year}-${month}-${day}`;
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return "";
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const formatDisplayDate = (value?: string) => {
    if (!value) return "-";
    const raw = String(value).trim();
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) return raw;

    // Plain date values only (YYYY-MM-DD): map directly.
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [year, month, day] = raw.split("-");
      return `${day}-${month}-${year}`;
    }

    // Timestamp values: render in IST to avoid one-day drift.
    if (raw.includes("T")) {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return raw;
      return d.toLocaleDateString("en-GB", { timeZone: "Asia/Kolkata" });
    }

    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear());
    return `${day}-${month}-${year}`;
  };

  const normalizeFromOptions = (value: string | undefined, options: string[]) => {
    const raw = (value || "").trim();
    if (!raw) return "";
    const found = options.find((option) => option.toLowerCase() === raw.toLowerCase());
    return found || raw;
  };

  const filteredStateOptions = useMemo(() => {
    const q = stateSearchValue.trim().toLowerCase();
    if (!q) return INDIAN_STATES;
    return INDIAN_STATES.filter((option) => option.toLowerCase().includes(q));
  }, [stateSearchValue]);

  const numberToWords = (num: number): string => {
    const units = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
    const teens = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "Ten", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const convertLessThanThousand = (n: number): string => {
      if (n === 0) return "";
      if (n < 10) return units[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return `${tens[Math.floor(n / 10)]} ${units[n % 10]}`.trim();
      return `${units[Math.floor(n / 100)]} Hundred ${convertLessThanThousand(n % 100)}`.trim();
    };
    if (!num) return "Zero Only";
    let n = Math.floor(num);
    let out = "";
    if (n >= 10000000) {
      out += `${convertLessThanThousand(Math.floor(n / 10000000))} Crore `;
      n %= 10000000;
    }
    if (n >= 100000) {
      out += `${convertLessThanThousand(Math.floor(n / 100000))} Lakh `;
      n %= 100000;
    }
    if (n >= 1000) {
      out += `${convertLessThanThousand(Math.floor(n / 1000))} Thousand `;
      n %= 1000;
    }
    if (n > 0) out += convertLessThanThousand(n);
    return `${out.trim()} Only`;
  };

  const generatePDF = async (elementId: string, fileName: string) => {
    const element = document.getElementById(elementId);
    if (!element) {
      alert("Unable to generate PDF. Content not found.");
      return;
    }

    try {
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
      });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      if (pdfHeight <= pdf.internal.pageSize.getHeight()) {
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      } else {
        let heightLeft = pdfHeight;
        let position = 0;
        const pageHeight = pdf.internal.pageSize.getHeight();

        pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - pdfHeight;
          pdf.addPage();
          pdf.addImage(imgData, "PNG", 0, position, pdfWidth, pdfHeight);
          heightLeft -= pageHeight;
        }
      }

      pdf.save(fileName);
    } catch (err) {
      console.error("PDF generation failed:", err);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  return (
    <DashboardLayout>
      <ActionToast action={toastAction} />
      <h1 className="list-title">Donations</h1>
      <p className="list-subtitle">
        Total: Rs {total.toLocaleString("en-IN")} from {filtered.length} donations
      </p>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="list-toolbar">
        <div className="search-wrap">
          <i className="fa-solid fa-magnifying-glass search-icon" />
          <input
            className="form-control search-input search-input-medium"
            placeholder="Search donor name or receipt no..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="table-shell table-responsive">
        <table className="table data-table mb-0">
          <thead>
            <tr>
              <th>Photo</th>
              <th>Receipt No</th>
              <th>Donor</th>
              <th>Mobile</th>
              <th>Amount</th>
              <th>Method</th>
              <th>Date</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((row) => (
              <tr key={row.id}>
                <td>
                  {row.photoUrl ? (
                    <img
                      src={toMediaUrl(row.photoUrl)}
                      alt={row.fullName || "Donor"}
                      style={{ width: "44px", height: "44px", objectFit: "cover", borderRadius: "8px", border: "1px solid #dfe5ef" }}
                    />
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </td>
                <td>{row.receiptNo || `DON-${row.id}`}</td>
                <td>{row.fullName}</td>
                <td>{row.mobile}</td>
                <td className="amount-cell">Rs {Number(row.amount || 0).toLocaleString("en-IN")}</td>
                <td>
                  <span className="data-pill">{row.paymentMethod || "-"}</span>
                </td>
                <td>{row.createdAt || "-"}</td>
                <td className="text-end">
                  <div className="action-icons">
                    <button type="button" className="icon-btn" title="View" onClick={() => setViewRow(row)}>
                      <i className="fa-regular fa-eye" />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      title="Edit"
                      onClick={() => {
                        setEditRow({ ...row });
                        setStateSearchValue(row.state || "");
                        setShowStateOptions(false);
                      }}
                    >
                      <i className="fa-regular fa-pen-to-square" />
                    </button>
                    {/* <button type="button" className="icon-btn" title="Receipt" onClick={() => alert("Receipt preview can be added next")}>
                      <i className="fa-regular fa-file-lines" />
                    </button> */}
                    <button type="button" className="icon-btn icon-btn-danger" title="Delete" onClick={() => onDelete(row.id)}>
                      <i className="fa-regular fa-trash-can" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="text-center py-4 text-muted">
                  No donations found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {viewRow && (
        <div className="dash-modal-overlay" onClick={() => setViewRow(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 className="dash-modal-title">Donation Details</h3>
              <button type="button" className="dash-modal-close" onClick={() => setViewRow(null)}>
                x
              </button>
            </div>
            <div className="dash-modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <div className="detail-label">Photo</div>
                  <div className="detail-value">
                    {viewRow.photoUrl ? (
                      <img
                        src={toMediaUrl(viewRow.photoUrl)}
                        alt={viewRow.fullName || "Donor"}
                        style={{ width: "96px", height: "96px", objectFit: "cover", borderRadius: "10px", border: "1px solid #dfe5ef" }}
                      />
                    ) : (
                      "-"
                    )}
                  </div>
                </div>
                <div className="detail-item"><div className="detail-label">Full Name</div><div className="detail-value">{viewRow.fullName || "-"}</div></div>
                <div className="detail-item"><div className="detail-label">Mobile</div><div className="detail-value">{viewRow.mobile || "-"}</div></div>
                <div className="detail-item"><div className="detail-label">Email</div><div className="detail-value">{viewRow.email || "-"}</div></div>
                <div className="detail-item"><div className="detail-label">Date of Birth</div><div className="detail-value">{formatDisplayDate(viewRow.dateOfBirth)}</div></div>
                <div className="detail-item"><div className="detail-label">Country</div><div className="detail-value">{viewRow.country || "-"}</div></div>
                <div className="detail-item"><div className="detail-label">State</div><div className="detail-value">{viewRow.state || "-"}</div></div>
                <div className="detail-item"><div className="detail-label">City</div><div className="detail-value">{viewRow.city || "-"}</div></div>
                <div className="detail-item"><div className="detail-label">Pin Code</div><div className="detail-value">{viewRow.pinCode || "-"}</div></div>
                <div className="detail-item"><div className="detail-label">Address</div><div className="detail-value">{viewRow.address || "-"}</div></div>
                <div className="detail-item"><div className="detail-label">PAN No</div><div className="detail-value">{viewRow.panNo || "-"}</div></div>
                <div className="detail-item"><div className="detail-label">Amount</div><div className="detail-value">{viewRow.amount ?? "-"}</div></div>
                <div className="detail-item"><div className="detail-label">Payment Method</div><div className="detail-value">{viewRow.paymentMethod || "-"}</div></div>
                <div className="detail-item"><div className="detail-label">Message</div><div className="detail-value">{viewRow.message || "-"}</div></div>
                <div className="detail-item"><div className="detail-label">Receipt No</div><div className="detail-value">{viewRow.receiptNo || "-"}</div></div>
                <div className="detail-item"><div className="detail-label">Pre-ARN</div><div className="detail-value">{viewRow.preARN || "-"}</div></div>
                <div className="detail-item"><div className="detail-label">Certificate No</div><div className="detail-value">{viewRow.certificateNo || "-"}</div></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {editRow && (
        <div className="dash-modal-overlay" onClick={() => setEditRow(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 className="dash-modal-title">Edit Donation</h3>
              <button type="button" className="dash-modal-close" onClick={() => setEditRow(null)}>
                x
              </button>
            </div>
            <div className="dash-modal-body">
              <div className="edit-grid">
                <div className="edit-field full">
                  <label className="edit-label">Photo</label>
                  {editRow.photoUrl ? (
                    <img
                      src={toMediaUrl(editRow.photoUrl)}
                      alt={editRow.fullName || "Donor"}
                      style={{ width: "110px", height: "110px", objectFit: "cover", borderRadius: "10px", border: "1px solid #dfe5ef", marginBottom: "10px" }}
                    />
                  ) : (
                    <div className="text-muted mb-2">No photo</div>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const previewUrl = URL.createObjectURL(file);
                      setEditRow({ ...editRow, photoFile: file, photoUrl: previewUrl });
                    }}
                  />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Full Name</label>
                  <input className="form-control" placeholder="Enter full name" value={editRow.fullName || ""} onChange={(e) => setEditRow({ ...editRow, fullName: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Mobile</label>
                  <input className="form-control" placeholder="Enter mobile number" value={editRow.mobile || ""} onChange={(e) => setEditRow({ ...editRow, mobile: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Email</label>
                  <input className="form-control" placeholder="Enter email address" value={editRow.email || ""} onChange={(e) => setEditRow({ ...editRow, email: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Date of Birth</label>
                  <input className="form-control" type="date" placeholder="Select date of birth" value={toInputDate(editRow.dateOfBirth)} onChange={(e) => setEditRow({ ...editRow, dateOfBirth: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Country</label>
                  <select className="form-select" value={normalizeFromOptions(editRow.country, COUNTRY_OPTIONS)} onChange={(e) => setEditRow({ ...editRow, country: e.target.value })}>
                    {COUNTRY_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                    {normalizeFromOptions(editRow.country, COUNTRY_OPTIONS) && !COUNTRY_OPTIONS.includes(normalizeFromOptions(editRow.country, COUNTRY_OPTIONS)) && (
                      <option value={normalizeFromOptions(editRow.country, COUNTRY_OPTIONS)}>{normalizeFromOptions(editRow.country, COUNTRY_OPTIONS)}</option>
                    )}
                  </select>
                </div>
                <div className="edit-field">
                  <label className="edit-label">State</label>
                  <div style={{ position: "relative" }}>
                    <input
                      className="form-control"
                      placeholder="Search or select state"
                      value={stateSearchValue}
                      onFocus={() => setShowStateOptions(true)}
                      onChange={(e) => {
                        setStateSearchValue(e.target.value);
                        setShowStateOptions(true);
                      }}
                      onBlur={() => {
                        const matched = INDIAN_STATES.find((option) => option.toLowerCase() === stateSearchValue.trim().toLowerCase());
                        if (matched) {
                          setStateSearchValue(matched);
                          setEditRow({ ...editRow, state: matched });
                        } else {
                          setStateSearchValue(editRow.state || "");
                        }
                        setTimeout(() => setShowStateOptions(false), 120);
                      }}
                    />
                    {showStateOptions && filteredStateOptions.length > 0 && (
                      <div
                        className="list-group"
                        style={{
                          position: "absolute",
                          zIndex: 20,
                          width: "100%",
                          maxHeight: "180px",
                          overflowY: "auto",
                          border: "1px solid #dfe5ef",
                          borderRadius: "8px",
                          marginTop: "6px",
                          background: "#fff",
                        }}
                      >
                        {filteredStateOptions.slice(0, 50).map((option) => (
                          <button
                            key={option}
                            type="button"
                            className="list-group-item list-group-item-action"
                            onMouseDown={(evt) => evt.preventDefault()}
                            onClick={() => {
                              setStateSearchValue(option);
                              setEditRow({ ...editRow, state: option });
                              setShowStateOptions(false);
                            }}
                          >
                            {option}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="edit-field">
                  <label className="edit-label">City</label>
                  <input className="form-control" placeholder="Enter city" value={editRow.city || ""} onChange={(e) => setEditRow({ ...editRow, city: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Pin Code</label>
                  <input className="form-control" placeholder="Enter pin code" value={editRow.pinCode || ""} onChange={(e) => setEditRow({ ...editRow, pinCode: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Address</label>
                  <input className="form-control" placeholder="Enter full address" value={editRow.address || ""} onChange={(e) => setEditRow({ ...editRow, address: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">PAN No</label>
                  <input className="form-control" placeholder="Enter PAN number" value={editRow.panNo || ""} onChange={(e) => setEditRow({ ...editRow, panNo: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Amount</label>
                  <input className="form-control" placeholder="Enter amount" type="number" value={editRow.amount || 0} onChange={(e) => setEditRow({ ...editRow, amount: Number(e.target.value) })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Payment Method</label>
                  <input className="form-control" placeholder="Enter payment method" value={editRow.paymentMethod || ""} onChange={(e) => setEditRow({ ...editRow, paymentMethod: e.target.value })} />
                </div>
                <div className="edit-field full">
                  <label className="edit-label">Message</label>
                  <textarea className="form-control" rows={3} placeholder="Enter message" value={editRow.message || ""} onChange={(e) => setEditRow({ ...editRow, message: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Receipt No</label>
                  <input className="form-control" placeholder="Enter receipt number" value={editRow.receiptNo || ""} onChange={(e) => setEditRow({ ...editRow, receiptNo: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Pre-ARN</label>
                  <input className="form-control" placeholder="Enter Pre-ARN" value={editRow.preARN || ""} onChange={(e) => setEditRow({ ...editRow, preARN: e.target.value })} />
                </div>
                <div className="edit-field">
                  <label className="edit-label">Certificate No</label>
                  <input className="form-control" placeholder="Enter certificate number" value={editRow.certificateNo || ""} onChange={(e) => setEditRow({ ...editRow, certificateNo: e.target.value })} />
                </div>
              </div>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setEditRow(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={() => {
                  if (!editRow?.receiptNo) {
                    alert("Please enter Receipt No first.");
                    return;
                  }
                  setShowReceiptModal(true);
                }}
              >
                Generate Receipt
              </button>
              <button
                type="button"
                className="btn btn-info"
                onClick={() => {
                  if (!editRow?.certificateNo) {
                    alert("Please enter Certificate No first.");
                    return;
                  }
                  setShowCertificateModal(true);
                }}
              >
                Generate Certificate
              </button>
              <button type="button" className="btn btn-primary" disabled={saving} onClick={onSave}>
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showReceiptModal && editRow && (
        <div className="dash-modal-overlay" onClick={() => setShowReceiptModal(false)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 className="dash-modal-title">Donation Receipt - {editRow.receiptNo}</h3>
              <button type="button" className="dash-modal-close" onClick={() => setShowReceiptModal(false)}>
                x
              </button>
            </div>
            <div className="dash-modal-body">
              <div id="receipt-content" className="receipt-container p-4" style={{ fontSize: "13px", lineHeight: 1.35 }}>
                <div className="row align-items-center mb-3" style={{ fontFamily: "'Segoe UI', sans-serif", fontSize: "13px" }}>
                  <div className="col-md-12 text-center">
                    <img src={HeaderBanner} alt="Srisailam Foundation Header" className="mb-2" style={{ width: "100%", maxWidth: "900px", height: "auto" }} />
                  </div>
                  <hr />
                </div>

                <h4 className="text-center mb-4 fw-bold" style={{ fontSize: "28px" }}>DONATION RECEIPT</h4>

                <p><strong>Receipt No.:</strong> {editRow.receiptNo || "-"}</p>
                <p><strong>Pre-Acknowledgement Reference Number (Pre-ARN):</strong> {editRow.preARN || "-"}</p>
                <p><strong>Date:</strong> {formatDisplayDate(editRow.createdAt || new Date().toISOString())}</p>
                <p><strong>Received with thanks from:</strong> {editRow.fullName || "-"}</p>
                <p><strong>Address:</strong> {editRow.address || "-"}</p>
                <p><strong>PAN of Donor:</strong> {editRow.panNo || "-"}</p>
                <p>
                  <strong>a sum of Rs.</strong> {Number(editRow.amount || 0).toLocaleString("en-IN")} (Rupees {numberToWords(Number(editRow.amount || 0))})
                </p>
                <p><strong>by way of:</strong> {editRow.paymentMethod || "-"}</p>
                <p><strong>towards donation to our organization.</strong></p>
                <p>This donation is eligible for deduction under Section 80G of the Income Tax Act, 1961.</p>

                <div className="mt-4">
                  <p><strong>80G URN:</strong> ABPCS2866RF20251</p>
                  <p><strong>Date of Approval:</strong> 19.04.2025</p>
                  <p><strong>Validity:</strong> Valid till 19.04.2028</p>
                  <p><strong>PAN of NGO:</strong> ABPCS2866R</p>
                </div>

                <div className="mt-2 text-center" style={{ float: "right" }}>
                  <p>For SRISAILAM FOUNDATION</p>
                  <img src={SignatureImage} alt="Signature and Seal" width={200} height={100} className="mt-2" />
                  <p className="mt-2">Authorized Signatory</p>
                </div>

                <p className="fst-italic text-center" style={{ paddingTop: "200px" }}>
                  Thank you for your generous support!
                </p>
              </div>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowReceiptModal(false)}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => generatePDF("receipt-content", `Recepit_${editRow.receiptNo || "Donation"}.pdf`)}
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {showCertificateModal && editRow && (
        <div className="dash-modal-overlay" onClick={() => setShowCertificateModal(false)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <div className="dash-modal-header">
              <h3 className="dash-modal-title">Donation Certificate - {editRow.certificateNo}</h3>
              <button type="button" className="dash-modal-close" onClick={() => setShowCertificateModal(false)}>
                x
              </button>
            </div>
            <div className="dash-modal-body">
              <div id="certificate-content" className="certificate-container p-4" style={{ fontSize: "13px", lineHeight: 1.35 }}>
                <div className="row align-items-center mb-3" style={{ fontFamily: "'Segoe UI', sans-serif", fontSize: "13px" }}>
                  <div className="col-md-12 text-center">
                    <img src={HeaderBanner} alt="Srisailam Foundation Header" className="mb-2" style={{ width: "100%", maxWidth: "900px", height: "auto" }} />
                  </div>
                  <hr />
                </div>

                <h4 className="text-center mb-2 fw-bold" style={{ fontSize: "28px" }}>DONATION CERTIFICATE</h4>

                <p><strong>Certificate No.:</strong> {editRow.certificateNo || "-"}</p>
                <p><strong>Date:</strong> {formatDisplayDate(editRow.createdAt || new Date().toISOString())}</p>
                <p><strong>Pre-Acknowledgement Reference Number (Pre-ARN):</strong> {editRow.preARN || "-"}</p>
                <p><strong>Name of Donee Organization:</strong> SRISAILAM FOUNDATION</p>
                <p><strong>PAN of Donee Organization:</strong> ABPCS2866R</p>
                <p><strong>80G Registration Number:</strong> ABPCS2866RF20251 / Validity Date - 19.04.2028</p>

                <h5 className="mt-2"><b>Details of Donor:</b></h5>
                <p><strong>Name:</strong> {editRow.fullName || "-"}</p>
                <p><strong>Address:</strong> {editRow.address || "-"}</p>
                <p><strong>PAN:</strong> {editRow.panNo || "-"}</p>
                <p><strong>Contact:</strong> {editRow.mobile || "-"}</p>

                <h5 className="mt-2"><b>Details of Donation:</b></h5>
                <p><strong>Mode of Receipt:</strong> {editRow.paymentMethod || "-"}</p>
                <p><strong>Amount:</strong> ₹{Number(editRow.amount || 0).toLocaleString("en-IN")}</p>
                <p><strong>Date of Receipt:</strong> {formatDisplayDate(editRow.createdAt || new Date().toISOString())}</p>
                <p className="mt-2">
                  <strong>Declaration:</strong> We hereby certify that the above donation has been received and will be utilized for charitable
                  purposes. The donation qualifies for deduction under Section 80G of the Income Tax Act, 1961.
                </p>

                <div className="mt-2 text-center" style={{ float: "right" }}>
                  <p>For SRISAILAM FOUNDATION</p>
                  <img src={SignatureImage} alt="Signature and Seal" width={200} height={100} />
                  <p>Authorized Signatory</p>
                </div>

                <div className="mt-1" style={{ paddingTop: "150px" }}>
                  <p><strong>Important Note to Donor:</strong></p>
                  <ul>
                    <li>Keep this certificate safe.</li>
                    <li>Quote this certificate while claiming deduction under Section 80G in your ITR.</li>
                    <li>Ensure that your PAN is correctly mentioned.</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="dash-modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setShowCertificateModal(false)}>
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => generatePDF("certificate-content", `Certificate_${editRow.certificateNo || "Donation"}.pdf`)}
              >
                Download PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default DonationList;
